import React from "react";
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    OutlinedInput,
    Chip,
    Box,
    Tooltip,
    SelectChangeEvent,
    FormHelperText
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {ECR_AGENT, RAG_AGENT} from '../../constants';
import styles from "./AgentSelector.module.css";

interface Props {
    selectedAgents: string[];
    onAgentsChange: (agents: string[]) => void;
    disabled?: boolean;
}

const AGENT_OPTIONS = [
    { value: RAG_AGENT, label: "Document Q&A", description: "Ask questions about process and procedure documents in a selected knowledge domain." },
    { value: ECR_AGENT, label: "ECR", description: "Track open ECRs, view cost and volume insights, and generate contract notes." },
    //{ value: NL2SQL_AGENT, label: "NL2SQL", description: "Converts natural language questions into SQL queries to retrieve information from a database." }
];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

export const AgentSelector = ({ selectedAgents, onAgentsChange, disabled = false }: Props) => {
    const handleChange = (event: SelectChangeEvent<typeof selectedAgents>) => {
        const value = event.target.value;
        onAgentsChange(typeof value === 'string' ? value.split(',') : value);
    };

    const hasError = selectedAgents.length === 0;

    return (
        <div className={styles.agentSelectorContainer}>
            <FormControl sx={{ minWidth: 200 }} size="small" disabled={disabled} error={hasError}>
                <InputLabel id="agent-selector-label">Agents</InputLabel>
                <Select
                    labelId="agent-selector-label"
                    id="agent-selector"
                    multiple
                    value={selectedAgents}
                    onChange={handleChange}
                    input={<OutlinedInput id="select-multiple-chip" label="Agents" />}
                    renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.5 }}>
                            {selected.length > 1 ? (
                                <Chip label={`${selected.length} selected`} size="small" />
                            ) : (
                                selected.map((value) => {
                                    const option = AGENT_OPTIONS.find(opt => opt.value === value);
                                    return (
                                        <Chip
                                            key={value}
                                            label={option?.label || value}
                                            size="small"
                                        />
                                    );
                                })
                            )}
                        </Box>
                    )}
                    MenuProps={MenuProps}
                >
                    {AGENT_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            <Checkbox checked={selectedAgents.indexOf(option.value) > -1} />
                            <ListItemText primary={option.label} />
                            <Tooltip
                                title={option.description}
                                placement="right"
                                arrow
                                slotProps={{
                                    tooltip: {
                                        sx: {
                                            width: "275px"
                                        }
                                    }
                                }}
                            >
                                <HelpOutlineIcon />
                            </Tooltip>
                        </MenuItem>
                    ))}
                </Select>
                <FormHelperText>{hasError ? "Please select at least one agent" : " "}</FormHelperText>
            </FormControl>
        </div>
    );
};
